import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Observable, catchError, switchMap, throwError } from 'rxjs';

import { AuthService } from '../services/auth.service';

const PUBLIC_PATHS = [
  '/api/auth/login',
  '/api/auth/refresh',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
  '/api/auth/activate',
  '/api/employer/register',
  '/api/identity/nin/verify',
  '/api/employee/activation-info',
  '/api/employee/complete-activation',
  '/api/pfas',
  '/api/activation/'
];

/** Endpoints that set or read the httpOnly refresh cookie. These must be sent
 *  with credentials so the browser stores (login/register) and returns
 *  (refresh/logout) the cookie. Business endpoints stay Bearer-only. */
const COOKIE_ENDPOINTS = [
  '/api/auth/login',
  '/api/auth/refresh',
  '/api/auth/logout',
  '/api/employer/register'   // also matches /register/link-existing
];

const ACTIVE_CONTEXT_HEADER = 'X-Active-Context';
const CLIENT_PLATFORM_HEADER = 'X-Client-Platform';
/** 'ios' | 'android' | 'web' — lets the API choose cookie (web) vs body-token (native). */
const CLIENT_PLATFORM = Capacitor.getPlatform();

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const isPublic = PUBLIC_PATHS.some(p => req.url.includes(p));

  let authed = isPublic
    ? req
    : attachContext(attachToken(req, auth.getAccessToken()), auth.activeContextId());

  authed = authed.clone({ setHeaders: { [CLIENT_PLATFORM_HEADER]: CLIENT_PLATFORM } });
  if (COOKIE_ENDPOINTS.some(p => req.url.includes(p))) {
    authed = authed.clone({ withCredentials: true });
  }

  return next(authed).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse && !isPublic && err.status === 401) {
        return refreshAndRetry(req, next, auth);
      }
      // 403s bubble up to the caller. Domain-specific handling (stale context,
      // PHONE_VERIFICATION_REQUIRED, etc.) lives at the call site so the
      // interceptor doesn't mask actionable errors with a silent retry.
      return throwError(() => err);
    })
  );
};

function attachToken(req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
  if (!token) { return req; }
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

function attachContext(req: HttpRequest<unknown>, contextId: string | null): HttpRequest<unknown> {
  if (!contextId) { return req; }
  return req.clone({ setHeaders: { [ACTIVE_CONTEXT_HEADER]: contextId } });
}

function refreshAndRetry(
  original: HttpRequest<unknown>,
  next:     HttpHandlerFn,
  auth:     AuthService
): Observable<ReturnType<HttpHandlerFn> extends Observable<infer T> ? T : never> {
  if (!auth.getRefreshToken()) {
    auth.clearTokensAndRedirect();
    return throwError(() => new Error('Session expired'));
  }

  return auth.refreshToken().pipe(
    switchMap(() => next(
      attachContext(attachToken(original, auth.getAccessToken()), auth.activeContextId())
    )),
    catchError(refreshErr => {
      auth.clearTokensAndRedirect();
      return throwError(() => refreshErr);
    })
  );
}
